import { EmptyState } from '@/components/ui';

/**
 * A small read-only table for the platform reports.
 *
 * The admin console writes its tables out by hand where each row carries links,
 * badges and actions -- markup that a generic component would fight. These
 * report tables carry none of that: they are numbers in columns, three of them,
 * with identical markup. Repeating the `<thead>` boilerplate three times is how
 * one of them quietly ends up with a different border or a left-aligned figure.
 *
 * Numeric columns are right-aligned and `tabular-nums`, so a column of money
 * lines up on the decimal point rather than ragging on digit width.
 */
export function ReportTable({ columns, rows, rowKey, empty }) {
  if (rows.length === 0) {
    return <EmptyState compact title={empty ?? 'Nothing to show for this period'} />;
  }

  return (
    <div className="overflow-x-auto">
      {/* No `data-table` minimum width: these are three or four short
          columns that fit their card, so a scrollbar only hid figures. */}
      <table className="w-full text-sm">
        <thead className="text-ink-muted text-left">
          <tr className="border-line border-b">
            {columns.map((column) => (
              <th
                key={column.key}
                className={`py-2 pr-4 font-medium ${column.numeric ? 'text-right' : ''}`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className="border-line border-b last:border-0">
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`py-2.5 pr-4 ${
                    column.numeric ? 'text-ink text-right tabular-nums' : 'text-ink'
                  }`}
                >
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
